import React, { useState } from 'react';
import { Camera, CreditCard, Upload, Shield, Building, Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

const Layout = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();

  const menuItems = [
    { name: "Главная", path: "/", icon: <Camera className="w-5 h-5" /> },
    { name: "Оплата", path: "/payment", icon: <CreditCard className="w-5 h-5" /> },
    { name: "Загрузка", path: "/upload", icon: <Upload className="w-5 h-5" /> },
    { name: "Документы", 
      items: [
        { name: "Политика", path: "/privacy", icon: <Shield className="w-5 h-5" /> },
        { name: "Реквизиты", path: "/requisites", icon: <Building className="w-5 h-5" /> }
      ]
    }
  ];

  const isActivePath = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <header className="bg-white shadow-sm fixed w-full top-0 z-50">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center px-4 h-16">
            {/* Logo */}
            <Link 
              to="/" 
              className="flex items-center space-x-2 font-medium"
            >
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 p-1.5 rounded">
                <Camera className="h-5 w-5 text-white" />
              </div>
              <span className="text-lg font-semibold text-gray-900">
                PhotoVideo
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-1">
              {menuItems.map((item) => (
                !item.items ? (
                  <Link
                    key={item.name}
                    to={item.path}
                    className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150 ${
                      isActivePath(item.path)
                        ? 'text-blue-600 bg-blue-50'
                        : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-1.5">
                      {item.icon}
                      <span>{item.name}</span>
                    </div>
                  </Link>
                ) : (
                  <div key={item.name} className="relative group">
                    <button className="px-3 py-2 rounded-md text-sm font-medium text-gray-600 hover:text-blue-600 hover:bg-gray-50">
                      {item.name}
                    </button>
                    <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 transform origin-top-left">
                      {item.items.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          className={`block px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 hover:text-blue-600 ${
                            isActivePath(subItem.path) ? 'bg-blue-50 text-blue-600' : ''
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            {subItem.icon}
                            <span>{subItem.name}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </nav>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-50"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMenuOpen && (
            <nav className="md:hidden border-t border-gray-100">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {menuItems.map((item) => (
                  !item.items ? (
                    <Link
                      key={item.name}
                      to={item.path}
                      className={`block px-3 py-2 rounded-md text-base font-medium ${
                        isActivePath(item.path)
                          ? 'text-blue-600 bg-blue-50'
                          : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                      }`}
                      onClick={() => setIsMenuOpen(false)}
                    >
                      <div className="flex items-center space-x-2">
                        {item.icon}
                        <span>{item.name}</span>
                      </div>
                    </Link>
                  ) : (
                    <div key={item.name} className="space-y-1">
                      <div className="px-3 py-2 text-sm font-medium text-gray-500">
                        {item.name}
                      </div>
                      {item.items.map((subItem) => (
                        <Link
                          key={subItem.name}
                          to={subItem.path}
                          className={`block px-3 py-2 rounded-md text-base font-medium pl-6 ${
                            isActivePath(subItem.path)
                              ? 'text-blue-600 bg-blue-50'
                              : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                          }`}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <div className="flex items-center space-x-2">
                            {subItem.icon}
                            <span>{subItem.name}</span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )
                ))}
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 py-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;